import {announce} from '@react-aria/live-announcer'
import Big from 'big.js'

import SearchSvg from '@/assets/icons/search.svg?react'
import OrderItemGroups from '@/components/OrderItemGroups/OrderItemGroups'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import Pagination from '@/layout/Pagination'
import HeadTags from '@/lib/head/HeadTags'
import {useLocale} from '@/lib/locale/locale'
import useOrders from '@/lib/order/hooks/useOrders'
import {type Order} from '@/models/orderModel'
import {OrderDetailsRoute, OrdersRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {formatLocaleDateTime} from '@/utils/datetime'
import useWindowSize from '@/utils/hooks/useWindowSize'
import useSearch from '@/utils/router/useSearch'

import style from './Orders.module.scss'

const ITEMS_PER_PAGE = 20
const ITEMS_PER_PAGE_MOBILE = 10

function OrderItem({order}: {order: Order & {value: Big}}) {
  const navigate = useNavigate()
  const subtotal = order.value
  const discountValue = Big(order.moreInfo?.amountDiscount ?? 0).times(-1)
  const deliveryFee = Big(order.moreInfo?.deliveryFee ?? 0)
  const totalBeforeTax = subtotal.add(deliveryFee).add(discountValue)
  const tax = Big(order.paymentInfo.amountTax ?? 0)
  const roundUp = Big(order.moreInfo?.amountRoundUp ?? 0)
  const total = totalBeforeTax.plus(tax).plus(roundUp)

  return (
    <div className={style.Order} key={order.id}>
      <div className={style.OrderDetails}>
        <div className={style.Header}>
          <div id={style.OrderId}>
            <h2
              className={style.OrderId}
              aria-label={`Order ${order.id}, total: $${total.toFixed(USD_DISPLAY_DECIMALS)}`}
            >
              Order #{order.id}
            </h2>
            <div className={style.Time}>
              Placed on{' '}
              {formatLocaleDateTime(order.createdAt, 'en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
          </div>
          <div className={style.TotalPrice} id={style.OrderTotalPrice}>
            <h3 aria-label={`Order total`}>Total price</h3>
            <div>
              <Price
                price={total}
                options={{
                  fractionDigits: USD_DISPLAY_DECIMALS,
                  exactFractionDigits: true,
                }}
              />
            </div>
          </div>
          <div className={style.ShippingInfo} id={style.OrderShippingInfo}>
            <h3
              className={style.ShippingLabel}
              aria-label={`Shipping to: ${order.shippingInfo?.name ?? 'Name not available'}`}
            >
              Ship to:
            </h3>
            <div className={style.Name}>
              <VisuallyHidden>Name: </VisuallyHidden>
              {order.shippingInfo?.name}
            </div>
          </div>
        </div>
        <div className={style.Divider} />
        <OrderItemGroups
          items={order.lineItems}
          showRetailer={false}
          showPrice={false}
          showQuantity={false}
          showTrackingInfo={false}
          showVariation={false}
          showBuyAgain
          order={order}
        />
      </div>
      <div className={style.Divider} />
      <div className={style.Actions}>
        <PrimaryButton
          size='lg'
          onPress={() => {
            void navigate({
              to: OrderDetailsRoute.fullPath,
              params: {orderId: order.id},
              search: {},
            })
          }}
          aria-label={`View details for order ${order.id}`}
        >
          View details
        </PrimaryButton>
      </div>
    </div>
  )
}

export default memo(function Orders() {
  const [locale] = useLocale()
  const router = useRouter()
  const {isMobile} = useWindowSize()

  const [q, setQ] = useState('')
  const deferredQ = useDeferredValue(q)

  const [page, setPage] = useSearch(OrdersRoute.id, 'page', {
    replace: false,
    resetScroll: true,
    hashScrollIntoView: {
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    },
  })

  const {data: orders} = useOrders(locale)

  const filteredOrders = useMemo(() => {
    if (!orders) return []
    return deferredQ
      ? orders.filter(order => {
          const searchTerm = deferredQ.toLowerCase()
          const matchesOrderId = order.id.toLowerCase().includes(searchTerm)
          const matchesShippingName = order.shippingInfo?.name
            ? order.shippingInfo.name.toLowerCase().includes(searchTerm)
            : false
          const matchesItems = order.lineItems.some(item =>
            item.title.toLowerCase().includes(searchTerm),
          )
          return matchesOrderId || matchesShippingName || matchesItems
        })
      : orders
  }, [orders, deferredQ])

  const totalOrders = useMemo(() => filteredOrders.length, [filteredOrders])
  const itemsPerPage = isMobile ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE
  const pageCount = totalOrders > 0 ? Math.ceil(totalOrders / itemsPerPage) : 1
  const latestPageCount = useLatest(pageCount)

  const currentPageItems = useMemo(() => {
    return filteredOrders.slice((page - 1) * itemsPerPage, page * itemsPerPage)
  }, [filteredOrders, page, itemsPerPage])

  useEffect(
    function correctPage() {
      if (!orders) return
      if (pageCount > 0 && page > pageCount) {
        void setPage(pageCount)
      }
    },
    [page, pageCount, orders],
  )

  const handlePageChange = useCallback((event: {selected: number}) => {
    const newPage = event.selected + 1
    void setPage(newPage)

    announce(`Page ${newPage} of ${latestPageCount.current}`)
  }, [])

  const hrefBuilder = useCallback(
    (pageNum: number) =>
      router.buildLocation({
        to: OrdersRoute.fullPath,
        search: {page: pageNum},
      }).href,
    [router],
  )

  return (
    <>
      <HeadTags title={`Orders (${totalOrders})`} />
      <main className={style.OrdersSection}>
        <div className={style.HeadingWrapper}>
          <h1 className={style.Heading}>Orders ({totalOrders})</h1>
          <form
            role='search'
            className={style.SearchWrapper}
            onSubmit={e => {
              e.preventDefault()
              announce(
                `${totalOrders.toString()} orders found${deferredQ ? ` for "${deferredQ}"` : ''}`,
              )
            }}
          >
            <label htmlFor='orders-search'>
              <VisuallyHidden>Search orders by order number, recipent, or product</VisuallyHidden>
            </label>
            <input
              id='orders-search'
              type='search'
              className={style.Search}
              value={q}
              onChange={e => {
                setQ(e.currentTarget.value)
              }}
              placeholder='Search by order, recipient, or product...'
              aria-describedby='search-results'
            />
            <button type='submit' className={style.SearchBtn} aria-label='Search orders'>
              <SearchSvg />
            </button>
          </form>
          <div className={style.PaginationWrapper}>
            {pageCount > 1 && (
              <Pagination
                onPageChange={handlePageChange}
                pageCount={pageCount}
                currentPage={page - 1}
                hrefBuilder={hrefBuilder}
              />
            )}
          </div>
        </div>

        <div id='search-results' role='status' aria-live='polite' aria-atomic='true'>
          <VisuallyHidden>
            {!orders && 'Loading orders...'}
            {orders?.length === 0 && 'No orders found.'}
            {filteredOrders.length === 0 &&
              orders &&
              orders.length > 0 &&
              `No orders found matching "${deferredQ}".`}
            {filteredOrders.length > 0 &&
              deferredQ &&
              `${totalOrders} orders found matching "${deferredQ}".`}
            {filteredOrders.length > 0 &&
              !deferredQ &&
              orders &&
              orders.length > 0 &&
              `Showing all ${totalOrders} orders.`}
          </VisuallyHidden>
        </div>
        {filteredOrders.length > 0 && (
          <div className={style.Orders}>
            {currentPageItems.map(order => (
              <OrderItem key={order.id} order={order} />
            ))}
          </div>
        )}

        {pageCount > 1 && filteredOrders.length > 0 && (
          <div className={style.PaginationWrapper}>
            <Pagination
              onPageChange={handlePageChange}
              pageCount={pageCount}
              currentPage={page - 1}
              hrefBuilder={hrefBuilder}
            />
          </div>
        )}
      </main>
    </>
  )
})
